import { useEffect } from 'react';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import * as PageItems from '../ducks/pageItems';
import * as Selection from '../ducks/selection';
import * as Project from '../ducks/project';
import * as Navigation from '../ducks/navigation';
import * as Sections from '../ducks/sections';
import * as Selectors from '../reducers/selectors';
import * as Pages from '../ducks/pages';
import ColorHash from 'color-hash';
import { keepSelection } from '../ducks/selection';
import { keepSelectedTab } from '../ducks/navigation';
import SectionsHelper from '../helpers/SectionsHelper';
import { addImageTag, removeImageTag, updateImageMeta } from '../ducks/images';
import PicabooConstants from '../common/PicabooConstants';
import pako from 'pako';
import { subscribe } from './graphql/subscriptions';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import Cookies from 'universal-cookie';
import { addMessage, uploadToBlurb } from '../ducks/messages';
import { showGenericErrorModal } from '../ducks/modals';
import { recordRumEvent } from '../utils/rum';

export const PAGE_UPDATED = 'ReturnedChangeSetProcessor/PAGE_UPDATED';
export const PAGES_UPDATED = 'ReturnedChangeSetProcessor/PAGES_UPDATED';
export const IMAGE_ADDED = 'ReturnedChangeSetProcessor/IMAGE_ADDED';
export const IMAGE_REMOVED = 'ReturnedChangeSetProcessor/IMAGE_REMOVED';
export const UPDATE_IMAGE_COUNTS =
  'ReturnedChangeSetProcessor/UPDATE_IMAGE_COUNTS';

Amplify.configure({
  API: {
    GraphQL: {
      endpoint: `${window.location.origin.replace(/^http/, 'ws')}/appsync-api/graphql`,
      region: 'us-west-1',
      defaultAuthMode: 'lambda',
    },
  },
});

const client = generateClient();

const ReturnedChangesetProcessor = ({
  projectPK,
  pages,
  changeRequestID,
  renderPage,
  dispatch,
  sections,
  pageItems,
  sectionID,
  spreads,
  spreadIndex,
}) => {
  let startTime;
  useEffect(() => {
    if (projectPK) {
      let token = new Cookies().get('web-editor-token');
      console.debug(`Subscribing to realtime events for project: ${projectPK}`);
      startTime = Date.now();
      const sub = client
        .graphql({
          query: subscribe,
          variables: { name: projectPK },
          authToken: token,
          authMode: 'lambda',
        })
        .subscribe({
          next: ({ data }) => {
            _realtimeUpdates(data.subscribe.data);
          },
          error: (error) => {
            console.error('AppSync error:', error);
            // TODO use full page error - waiting on Anain
            dispatch(showGenericErrorModal());
          },
        });
      return () => {
        console.debug(
          `Unsubscribing from realtime events for project: ${projectPK}`,
        );
        sub.unsubscribe();
      };
    }
  }, [projectPK, dispatch]);

  function _ignoreChanges(data, change) {
    const alwaysProcess = [
      'PAGE_IMAGE_USAGE_CHANGED',
      'IMAGE_REMOVED',
      'PAGE_REFLOWED',
    ];

    //stickers...
    if (change.type === 'PAGE_ITEM_ADDED') {
      if (
        change.changes.item &&
        change.changes.item.type ===
          PicabooConstants.WELL_TYPES.INTERACTIVE_ELEMENT
      ) {
        return false;
      }
    }

    if (alwaysProcess.includes(change.type)) {
      return false;
    }

    return data.changeRequestID == changeRequestID;
  }

  async function _realtimeUpdates(rawData) {
    let a = JSON.parse(rawData);
    const base64data = atob(a.data);

    const changesetJSON = pako.inflate(base64data, { to: 'string' });
    let data = JSON.parse(changesetJSON);

    if (data.message) {
      dispatch(addMessage(data.message.jobID, data.message));

      if (data.message.status === 'A') {
        dispatch(uploadToBlurb(data.message.jobID));
        let timeDuration = Date.now() - startTime;
        recordRumEvent('complete_project_event', {
          projectRenderingTime: timeDuration,
        });
      }
    }

    if (data.changes) {
      for (let change of data.changes) {
        if (_ignoreChanges(data, change)) {
          continue;
        }
        let msg = null;
        let sendRender = false;
        let renderPageID = null;
        switch (change.type) {
          case 'PAGE_ITEM_ADDED':
            const items = [fromJS(change.changes.item)];
            msg = {
              type: PageItems.ADD_PAGE_ITEMS_BY_OTHER,
              page: change.changes.pageID,
              pageItems: items,
              propagate: false,
            };

            renderPageID = change.changes.pageID;
            sendRender = true;
            break;
          case 'PAGE_LAST_UPDATE_CHANGED': {
            msg = Pages.updatePageLastUpdate(change.changes);
            break;
          }
          case 'PAGE_ITEM_UPDATED': {
            const pageItems = change.changes.map((item) => fromJS(item)); // needs to be array of immutable maps
            msg = PageItems.updatePageItemsByOther(pageItems);

            //If any of these items are on the current page we have to reload
            let pageFound = false;
            for (let item of change.changes) {
              if (pages.get(item.pageID) && renderPage === item.pageID) {
                pageFound = true;
                break;
              }
            }

            if (pageFound) {
              dispatch(msg);
              msg = Pages.reloadSpread();
            }

            break;
          }
          case 'PAGE_THUMB_UPDATED':
            msg = {
              type: Pages.PAGE_THUMB_UPDATED,
              pageID: change.changes.pageID,
              thumbURL: change.changes.thumbnailURL,
              numBlankPages: change.changes.numBlankPages,
            };

            dispatch(msg);

            let sectionID = SectionsHelper.findSectionIDWithPage(
              sections,
              change.changes.pageID,
            );
            msg = Sections.updateSectionsPageThumb(
              sectionID,
              change.changes.pageID,
              change.changes.thumbnailURL,
              change.changes.numBlankPages,
            );

            break;
          case 'PAGE_REFLOWED_BY_OTHER': {
            let pageID = change.changes;
            if (!data.reloaded && pages.get(pageID)) {
              msg = Pages.reloadSpread();
              data.reloaded = true;
            }
            renderPageID = pageID;
            break;
          }
          case 'PAGE_UPDATED':
            msg = {
              type: PAGE_UPDATED,
              page: change.changes,
              suppressThumb: true,
            };

            renderPageID = change.changes.pageID
              ? change.changes.pageID
              : change.changes.id;
            break;

          case 'PAGES_UPDATED':
            msg = {
              type: PAGES_UPDATED,
              pages: change.changes,
              replace: false,
            };

            break;

          case 'IMAGE_ADDED':
            if (change.changes.approvalStatus !== 0) {
              msg = {
                type: IMAGE_ADDED,
                image: change.changes,
              };

              //if this image was a sticker and it is used on a current page then we need to re-render
              if (msg.image.serverImage == 1) {
                const itemOnPage = pageItems.some(function (pageItem) {
                  return msg.image.imageID === pageItem.get('id');
                });
                if (itemOnPage) {
                  sendRender = true;
                }
              }
            }

            break;

          case 'IMAGE_REMOVED':
            msg = {
              type: IMAGE_REMOVED,
              image: change.changes,
            };

            break;

          case 'PAGES_SELECTED':
            let colorHash = new ColorHash();

            msg = {
              type: Selection.SELECTED_PAGE_BY_OTHER,
              user: change.changes.user,
              colorHex: colorHash.hex(data.changeRequestID),
              clientID: data.changeRequestID,
              pages: change.changes.pages,
            };

            break;

          case 'PAGE_ITEMS_SELECTED':
            colorHash = new ColorHash();

            msg = {
              type: Selection.SELECTED_PAGE_ITEMS_BY_OTHER,
              user: change.changes.user,
              colorHex: colorHash.hex(data.changeRequestID),
              clientID: data.changeRequestID,
              pageItems: change.changes.pageItems,
            };

            break;
          case 'PAGE_IMAGE_USAGE_CHANGED':
            msg = {
              type: UPDATE_IMAGE_COUNTS,
              spreadID: change.changes.spreadID,
              deltas: change.changes.deltas,
            };

            break;

          case 'PAGE_ITEM_DELETED':
            msg = PageItems.removePageItemsByOther(change.changes.pageID, [
              change.changes.itemID,
            ]);
            break;
          case 'UPDATE_PAGE_NUMBER_SETTINGS':
            msg = Project.updatePageNumberStyle(change.changes);
            sendRender = true;
            break;

          case 'HEADERS_CHANGED':
            msg = {
              type: Project.HEADERS_CHANGED,
              changes: change,
            };

            msg = Project.loadProject(projectPK, sectionID);
            break;

          case 'NAME_CHANGED':
            msg = {
              type: Project.NAME_CHANGED,
              name: change.changes['name'],
              author: change.changes['author'],
            };

            break;
          case 'SECTION_SPREADS_CHANGED':
            msg = change.changes;
            msg.type = Sections.SECTION_SPREADS_CHANGED;
            break;
          case 'SECTION_ADDED':
            let spreadID = spreads.get(spreadIndex);
            msg = Project.loadProject(projectPK, sectionID, spreadID);
            msg = keepSelectedTab(keepSelection(msg));

            break;
          case 'SECTION_UPDATED':
            if (Array.isArray(change.changes)) {
              for (let c of change.changes) {
                if (c.PageShift === true) {
                  msg = Project.loadProject(projectPK, sectionID);
                  break;
                } else if (c.id && c.hasOwnProperty('sortOrder')) {
                  dispatch(
                    Sections.updateSectionSortOrder(
                      change.changes.id,
                      change.changes.sortOrder,
                    ),
                  );
                  msg = Sections.reorderSections();
                }
              }
            } else if (
              change.changes.id &&
              change.changes.hasOwnProperty('sortOrder')
            ) {
              dispatch(
                Sections.updateSectionSortOrder(
                  change.changes.id,
                  change.changes.sortOrder,
                ),
              );
              msg = Sections.reorderSections();
            }
            break;
          case 'SECTION_DELETED':
            msg = Sections.deleteSection(change.changes.sectionID);
            break;
          case 'SECTIONS_LOCKED':
            msg = [
              Sections.updateSectionsLocked(change.changes),
              Navigation.setNavigation(),
            ];
            break;
          case 'UPDATE_COVERS':
            msg = Project.updateCoverTypes(change.changes);
            break;
          case 'IMAGE_TAG_ADDED':
            msg = addImageTag(change.changes.imageID, change.changes.tag);
            break;
          case 'IMAGE_TAG_REMOVED':
            msg = removeImageTag(change.changes.imageID, change.changes.tag);
            break;
          case 'IMAGE_META_UPDATED':
            msg = updateImageMeta(change.changes.imageID, change.changes.notes);
            break;
        }

        if (msg != null) {
          if (msg.constructor !== Array) {
            msg = [msg];
          }
          msg.forEach((change) => {
            change.APPLYING_CHANGES = true;
            dispatch(change);
          });
          if (sendRender) {
            dispatch(Navigation.renderCanvasFromStore(renderPageID));
          }
        }
      }
    }
  }
  return null;
};

export default connect((state) => ({
  projectPK: Selectors.projectMetadataSelector(state).get('projectPK'),
  changeRequestID: state.changesets.settings.changeRequestID,
  sectionID: Selectors.sectionIdSelector(state),
  spreadIndex: Selectors.spreadIndexSelector(state),
  spreads: Selectors.activeSpreadSelector(state),
  pageItems: Selectors.pageItemsSelector(state),
  sections: Selectors.projectSectionsSelector(state),
  pages: Selectors.pagesSelector(state),
  renderPageID: state.navigation.renderPage,
}))(ReturnedChangesetProcessor);
